import type { Account } from '@foundershub/api-client';
import { useListBvLogsQuery, useListDvLogsQuery } from '@foundershub/api-client';
import { useAccountRequired } from '../store';

interface VStatus {
  canRequest: boolean;
  status: Status;
  visible: boolean;
  expirationDate?: string | null;
}

export interface ModalProps {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}

export function useVerificationStatus(): { bv: VStatus; dv: VStatus } {
  const account = useAccountRequired();

  const { data: bvLogs } = useListBvLogsQuery({ account_pk: account.id });
  const { data: dvLogs } = useListDvLogsQuery({ account_pk: account.id });

  const currentBvLog = bvLogs && bvLogs.length > 0 ? bvLogs[0] : undefined;
  const currentDvLog = dvLogs && dvLogs.length > 0 ? dvLogs[0] : undefined;

  const bvState = account['bv_state']?.toLowerCase() as Lowercase<State> | undefined;
  const dvState = account['dv_state']?.toLowerCase() as Lowercase<State> | undefined;

  const bv = {
    canRequest: account.bv_state === null,
    visible: true,
    status: bvState && bvState in stateToStatus ? stateToStatus[bvState] : 'not-verified',
    expirationDate: currentBvLog?.ov_expiration_datetime,
  };
  const dv = {
    canRequest: account.dv_state === null,
    visible: true,
    status: dvState && dvState in stateToStatus ? stateToStatus[dvState] : 'not-verified',
    expirationDate: currentDvLog?.ov_expiration_datetime,
  };

  if (!account.is_incorporated || !account.street_address) {
    bv.visible = false;
    bv.canRequest = false;
  }

  if (bv.status !== 'verified') {
    dv.visible = false;
    dv.canRequest = false;
  }

  return { bv, dv };
}

export type Status = 'not-verified' | 'appeal-in-review' | 'in-review' | 'verified' | 'failed';

type State = NonNullable<Account['dv_state']>;

const stateToStatus: Record<Lowercase<State>, Status> = {
  accepted: 'in-review',
  'not started': 'in-review',
  'in progress': 'in-review',
  pass: 'verified',
  fail: 'failed',
  error: 'in-review',
  cancelled: 'not-verified',
  'pass with override': 'verified',
  'fail with override': 'failed',
  'appeal submitted': 'appeal-in-review',
  'onevet took too long': 'in-review',
  'pass with restrictions': 'verified',
  expired: 'failed',
};
